import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Save, Loader2, BookOpen, X, Plus, FlaskConical, Zap, ListOrdered } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { AIQueryLibrary } from "../../notifications/AIQueryLibrary";
import { WorkflowTestSection, TestResultData } from "./WorkflowTestSection";
import { BoundaryBox } from "../BoundingBoxEditor";
import { toast } from "sonner";

interface WorkflowProps {
  device: any;
  deviceId: string;
  questionValue: string;
  onQuestionChange: (v: string) => void;
  onSaveQuery: () => void;
  onGenerateQuery: () => void;
  onSaveBoundingBoxes: (boxes: BoundaryBox[]) => Promise<void>;
  isUpdating: boolean;
  canEdit: boolean;
  notification: { result: string; reason: string; message: string; subject: string };
  updateField?: (field: string, value: string | number) => Promise<void>;
  aiWorkflow?: string;
}

interface Step {
  tag: string;
  query: string;
}

function parseSteps(device: any): Step[] {
  // Parse tags
  let tags: string[] = [];
  const storedTags = device?.notification_data_tags;
  if (Array.isArray(storedTags)) tags = storedTags;
  else if (typeof storedTags === "string") {
    try { tags = JSON.parse(storedTags); } catch { tags = []; }
  }

  // Parse queries - could be a JSON array string or a plain string
  let queries: string[] = [];
  const storedQ = device?.notification_question || "";
  if (storedQ) {
    try {
      const parsed = JSON.parse(storedQ);
      if (Array.isArray(parsed)) queries = parsed;
      else queries = [storedQ];
    } catch {
      queries = [storedQ];
    }
  }

  // Build paired steps from tags (tags are the source of truth for count)
  if (tags.length === 0 && queries.length === 0) return [];

  const maxLen = Math.max(tags.length, queries.length);
  const steps: Step[] = [];
  for (let i = 0; i < maxLen; i++) {
    steps.push({
      tag: tags[i] || `Step ${i + 1}`,
      query: queries[i] || "",
    });
  }
  return steps;
}

export const ChainOfThoughtWorkflow = ({
  device,
  deviceId,
  isUpdating,
  canEdit,
  updateField,
  aiWorkflow,
}: WorkflowProps) => {
  const [steps, setSteps] = useState<Step[]>(() => parseSteps(device));
  const [newTag, setNewTag] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [queryLibraryStepIndex, setQueryLibraryStepIndex] = useState<number | null>(null);
  const [testResult, setTestResult] = useState<TestResultData | null>(null);
  const [openSteps, setOpenSteps] = useState<Record<number, boolean>>(() => {
    const open: Record<number, boolean> = {};
    steps.forEach((_, i) => { open[i] = true; });
    return open;
  });

  const saveSteps = async (updatedSteps: Step[]) => {
    if (!updateField) return;
    setIsSaving(true);
    try {
      const tags = updatedSteps.map(s => s.tag);
      const queries = updatedSteps.map(s => s.query);
      await updateField("notificationDataTags", JSON.stringify(tags));
      await updateField("notificationQuestion", JSON.stringify(queries));
      toast.success("Steps saved successfully");
    } catch {
      toast.error("Failed to save steps");
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddStep = () => {
    const trimmed = newTag.trim();
    if (!trimmed || steps.length >= 5 || steps.some(s => s.tag === trimmed)) return;
    const updated = [...steps, { tag: trimmed, query: "" }];
    setSteps(updated);
    setNewTag("");
    setOpenSteps(prev => ({ ...prev, [updated.length - 1]: true }));
    saveSteps(updated);
  };

  const handleRemoveStep = (index: number) => {
    const updated = steps.filter((_, i) => i !== index);
    setSteps(updated);
    saveSteps(updated);
  };

  const handleQueryChange = (index: number, query: string) => {
    const updated = steps.map((s, i) => (i === index ? { ...s, query } : s));
    setSteps(updated);
  };

  const handleSaveStepQuery = (index: number) => {
    saveSteps(steps);
  };

  const handleQueryLibrarySelect = (query: string, dataTags?: string[]) => {
    if (queryLibraryStepIndex === null) return;
    const updated = steps.map((s, i) => (i === queryLibraryStepIndex ? { ...s, query } : s));
    setSteps(updated);
    setQueryLibraryStepIndex(null);
    saveSteps(updated);
  };

  return (
    <div className="space-y-4">
      {/* Chain of Thought Steps Card */}
      <Card className="border bg-muted/50">
        <CardHeader className="py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ListOrdered className="h-4 w-4 text-brand-500" />
              <CardTitle className="text-base">Chain of Thought Steps</CardTitle>
              <span className="text-xs text-muted-foreground">({steps.length}/5)</span>
            </div>
            {isSaving && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" /> Saving...
              </span>
            )}
          </div>
        </CardHeader>
        <CardContent className="py-3 space-y-3">
          <p className="text-xs text-muted-foreground">
            Define up to 5 sequential steps. Each step has a tag name and an AI query that will be processed in order.
          </p>

          {/* Steps List */}
          {steps.map((step, index) => (
            <Collapsible
              key={index}
              open={openSteps[index] !== false}
              onOpenChange={(open) => setOpenSteps(prev => ({ ...prev, [index]: open }))}
            >
              <div className="rounded-lg border bg-background">
                <div className="flex items-center justify-between px-3 py-2">
                  <CollapsibleTrigger className="flex items-center gap-2 flex-1 text-left">
                    <Badge variant="outline" className="text-xs font-mono shrink-0">
                      {index + 1}
                    </Badge>
                    <span className="font-medium text-sm truncate">{step.tag}</span>
                  </CollapsibleTrigger>
                  <div className="flex items-center gap-1">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            disabled={!canEdit}
                            onClick={() => setQueryLibraryStepIndex(index)}
                          >
                            <BookOpen className="h-3.5 w-3.5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent><p>Query Library</p></TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    {canEdit && (
                      <button
                        onClick={() => handleRemoveStep(index)}
                        className="text-muted-foreground hover:text-destructive p-1"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>
                <CollapsibleContent>
                  <div className="px-3 pb-3 space-y-2">
                    <Textarea
                      value={step.query}
                      onChange={(e) => handleQueryChange(index, e.target.value)}
                      disabled={isUpdating || !canEdit}
                      className="min-h-[120px] resize-none text-sm"
                      placeholder={`Enter AI query for "${step.tag}"...`}
                    />
                    <div className="flex justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSaveStepQuery(index)}
                        disabled={isUpdating || !canEdit || isSaving}
                        className="h-7 text-xs bg-component-background text-foreground border border-foreground"
                      >
                        <Save className="h-3 w-3 mr-1" />
                        Save
                      </Button>
                    </div>
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          ))}

          {/* Add Step */}
          {steps.length < 5 && (
            <div className="flex gap-2">
              <Input
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAddStep()}
                placeholder="Enter step tag name"
                disabled={isUpdating || !canEdit}
                className="flex-1"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={handleAddStep}
                disabled={isUpdating || !canEdit || !newTag.trim()}
                className="bg-component-background text-foreground border border-foreground"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Step
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Test Section */}
      <WorkflowTestSection deviceId={deviceId} device={device} disabled={isUpdating || !canEdit} />


      {/* Query Library Dialog */}
      <AIQueryLibrary
        isOpen={queryLibraryStepIndex !== null}
        onClose={() => setQueryLibraryStepIndex(null)}
        aiWorkflow={aiWorkflow || device?.ai_workflow}
        onSelectQuery={handleQueryLibrarySelect}
      />
    </div>
  );
};
